import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseChangeType } from '@databases/types/redux';
import { fieldTypeSupportsDefaultValue } from '@databases/utils/database/field-types';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { addChangeToDraft } from '@databases/redux/changesets/actions';
import DefaultValueSetting from './DefaultValueSetting.tsx';
import { getFieldSettingsOptionsByType } from './FieldSettingsOptionsByType.tsx';
import AllowMultiToggle from './shared/AllowMultiToggle.tsx';
import { getAllowMultiToggleMessages } from './shared/getAllowMultiToggleMessages.ts';

const containerStyles = xcss({
  borderTop: `1px solid ${token('color.border')}`,
  paddingInline: 'space.200',
  paddingBlock: 'space.100'
});

type FieldSettingsMenuContentProps = {
  contentId: string;
  fieldId: string;
};

const FieldSettingsMenuContent: FunctionComponent<FieldSettingsMenuContentProps> = ({
  contentId,
  fieldId
}) => {
  const databaseDraft = defaultStoreUtils.useSelector((state) =>
    selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true
    })
  );

  const field = getDatabaseFieldRequired(databaseDraft, fieldId);
  const dispatch = defaultStoreUtils.useDispatch();
  const handleOnChangeAllowMulti = useCallback(
    (isMulti: boolean) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_IS_MULTI,
            fieldId,
            isMulti
          },
          {
            fieldSettingsAnalytics: {
              source: 'fieldContextualMenu',
              fieldType: field.type
            }
          }
        )
      );
    },
    [contentId, dispatch, field.type, fieldId]
  );

  const allowMultiMessages = getAllowMultiToggleMessages(field.type);
  const dateFieldSettingsEnabled = getExperimentValue('dateFieldSettings', 'enabled', {
    fireExperimentExposure: true
  });
  const fieldSettingsOptions = getFieldSettingsOptionsByType(
    contentId,
    field,
    dateFieldSettingsEnabled
  );
  const shouldRenderDefaultValue = fieldTypeSupportsDefaultValue(field.type);

  return (
    <Stack testId="field-settings-menu-content" xcss={containerStyles}>
      {fieldSettingsOptions}
      <Stack space="space.050">
        {allowMultiMessages && (
          <AllowMultiToggle
            labelMessage={allowMultiMessages.labelMessage}
            onChange={handleOnChangeAllowMulti}
            tooltipMessage={allowMultiMessages.tooltipMessage}
            value={Boolean(field.settings.multi)}
          />
        )}
        {shouldRenderDefaultValue && (
          <Box paddingBlockEnd="space.025">
            <DefaultValueSetting
              contentId={contentId}
              database={databaseDraft}
              dateFieldSettingsEnabled={dateFieldSettingsEnabled}
              field={field}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default FieldSettingsMenuContent;
